import React, { useEffect, useState } from "react";
import Sidebar from "./SideBar/Sidebar";
import MainContent from "./MainContent/MainContent";
import ProjectPage from "../../Components/Projects/ProjectDetailed/ProjectPage";
import { useFirebaseDatabaseRootData } from "../../context/FirebaseDatabaseRootDataContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import useGitHubSkills from "../../hooks/useGitHubSkills";
import Preloader from "./PreLoader/PreLoader";
import Header from "../../Layouts/Header/Header";
import NowPlaying from "../../Components/Spotify/NowPlaying";
import useSpotifyTrack from "../../hooks/useSpotifyTrack";
import useDominantColor from "../../hooks/useDominantColor";
import "./HomePage.css";

const MOBILE_BREAKPOINT = "(max-width: 960px)";

const SKILLS_ANCHOR = { key: "skills", href: "#skills", title: "Skills" };

function injectSkillsAnchor(anchors) {
  if (!anchors) return anchors;
  // Remove DSA
  const filtered = anchors.filter((a) => a.href !== "#dsa");
  if (filtered.some((a) => a.href === "#skills")) return filtered;
  // Insert Skills after experience, before projects
  const experienceIdx = filtered.findIndex((a) => a.href === "#experience");
  const insertAt = experienceIdx >= 0 ? experienceIdx + 1 : filtered.length;
  return [...filtered.slice(0, insertAt), SKILLS_ANCHOR, ...filtered.slice(insertAt)];
}

const HomePage = () => {
  const { rootData, rootDataLoading, rootDataError } =
    useFirebaseDatabaseRootData();
  const { loading: skillsLoading } = useGitHubSkills();
  const [currentSection, setCurrentSection] = useState("#about");
  const [scrollToTarget, setScrollToTarget] = useState(null);
  const isMobileView = useMediaQuery(MOBILE_BREAKPOINT);
  const [minTimePassed, setMinTimePassed] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const { track, isPlaying } = useSpotifyTrack();
  const dominantColor = useDominantColor(track?.albumArt);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimePassed(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleAnchorClick = (sectionId) => {
    setCurrentSection(sectionId);
    setScrollToTarget(sectionId);
  };

  const handleProjectSelect = (project) => {
    console.log("Project selected:", project);
    setSelectedProject(project);
  };

  const handleBackToList = () => {
    setSelectedProject(null);
  };

  const isLoading = !minTimePassed || rootDataLoading || skillsLoading;

  if (rootDataError) {
    return (
      <div className="home-page-container error">
        <div className="error-message">
          Error loading portfolio data: {rootDataError}
        </div>
      </div>
    );
  }

  if (!isLoading && (!rootData || !rootData.about || !rootData.homePageAnchors)) {
    return (
      <div className="home-page-container error">
        <div className="error-message">Missing essential portfolio data.</div>
      </div>
    );
  }

  return (
    <>
      <Preloader isExiting={!isLoading} />
      <Header />

      <div className={`home-page-container ${isLoading ? "loading" : "loaded"}`}>
        {track?.albumArt && (
          <div
            className={`album-art-bg ${dominantColor !== null ? "visible" : ""}`}
            style={{ backgroundImage: `url(${track.albumArt})` }}
          />
        )}
        <NowPlaying track={track} isPlaying={isPlaying} />
        {!isLoading && rootData && (
          <>
            <Sidebar
              name={rootData.about.name}
              title={rootData.about.title}
              anchorItems={injectSkillsAnchor(rootData.homePageAnchors)}
              currentSelection={currentSection}
              setCurrentSection={setCurrentSection}
              onAnchorChange={handleAnchorClick}
              isVisible={!isMobileView}
            />
            <MainContent
              isMobileView={isMobileView}
              scrollToTarget={scrollToTarget}
              onSectionChange={setCurrentSection}
              onProjectSelect={handleProjectSelect}
            />
          </>
        )}
      </div>

      {selectedProject && (
        <div className="project-detail-overlay">
          <ProjectPage project={selectedProject} onBack={handleBackToList} />
        </div>
      )}
    </>
  );
};

export default HomePage;